import { useEffect } from "react"
import { signal } from "@preact/signals-react"

import { addNotification } from "../stores/notification-store"
import { fetchComponentConfigSchema, fetchComponentConfigSchemaLocal } from "../utils/api"

export const componentConfigSchema = signal({})
export const isMigrated = signal(false)
export const error = signal(null)
export const loading = signal(false)
export const modified = signal(false) // allows a component to flag the data as modified so subsequent calls to the hook will reload it
const version = signal(null)
export const parameterHash = signal(null)

const fetchData = async (businessEntityId, utgVersion, config, accessToken) => {

    const hash = await import('object-hash').then(module => module.default || module)
    // if any parameters have changed then flag as modified so that the hook will reload the data
    const currentParametersHash = hash([businessEntityId, utgVersion, config, accessToken])
    if (currentParametersHash != parameterHash.value) {
        modified.value = true
        parameterHash.value = currentParametersHash
    }

    // short-circuit if we have a pending request, are waiting for parameters or have already received an error
    if (loading.value || !businessEntityId || !utgVersion || !config || !accessToken || error.value || !modified.value) {
        return
    }

    // short-circuit if we already have the data
    if (version.value == utgVersion) {
        return
    }
    
    try {
        loading.value = true
        version.value = utgVersion; // allows us to short-circuit future calls when the data is already loaded
        
        // if app config file has loadLocalComponentConfigSchema enabled, load that instead of from database. This is useful for development/debugging schema updates
        let configSchemaResult
        if (config.loadLocalComponentConfigSchema) {
            console.log("Loading local componentConfigSchema instead from database")
            const resultConfigSchema = await fetchComponentConfigSchemaLocal(utgVersion)
            configSchemaResult = resultConfigSchema.properties
            isMigrated.value = !!resultConfigSchema['s4-migrated']
        }
        else {
            console.log("Loading componentConfigSchema from database")
            const schema = JSON.parse((await fetchComponentConfigSchema(config, utgVersion)).Schema)
            configSchemaResult = schema.properties
            // if s4-migrated attribute there and set to true the schema has been migrated, otherwise set isMigrated to false
            isMigrated.value = !!schema['s4-migrated']
        }
        componentConfigSchema.value = configSchemaResult
        return configSchemaResult

    }
    catch(err) {
        error.value = err
        console.log(err)
        
        addNotification(`Unable to load data due to the following error - ${err}`)
    }
    finally {
        loading.value = false
        modified.value = false
    }  
    
}

export default function useComponentConfigSchema(businessEntityId, utgVersion, config, accessToken) {
    useEffect(() => {
        fetchData(businessEntityId, utgVersion, config, accessToken)
    }, [businessEntityId, utgVersion, config, accessToken])

    return {
        componentConfigSchema, isMigrated, loading, error
    }
}